// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import firefoxPrintFix from "@insite/client-framework/Common/Utilities/printFirefox";
import systemZIndex from "@west/XDL/PreStyle/zIndex";

interface GetScriptsOptions {
    appendTo?: Element;
    id?: string;
    async?: boolean;
}

/**
 * To load JS script with callback.
 * you can pass one url or multiple
 * Example:
 * getScripts.urls([ // url1, url2]).then(() => { //callback handler });
 */
export const getScripts = {
    url: (url: string, options?: GetScriptsOptions) => {
        return new Promise((resolve, reject) => {
            if (typeof document !== "undefined") {
                const script = document.createElement("script");
                script.type = "text/javascript";
                script.src = url;
                Object.keys(options || {}).forEach(key => {
                    if (key in (options || {})) {
                        (script as any)[key] = (options as any)[key];
                    }
                });

                script.addEventListener("load", () => resolve(script), false);
                script.addEventListener(
                    "error",
                    () => {
                        reject(script);
                        document.body.removeChild(script);
                    },
                    false,
                );

                if (options?.appendTo) {
                    options.appendTo.appendChild(script);
                } else {
                    document.body.appendChild(script);
                }
            }
        });
    },
    urls: (urls: string[], options?: GetScriptsOptions) => {
        return Promise.all(urls.map(url => getScripts.url(url, options)));
    },
};

export const isSmallMobileScreen = () =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 480px)").matches;

export const isMobileScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

export const isTabletScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 1024px)").matches;

export const isWideScreen = () => typeof window !== "undefined" && window.matchMedia("(min-width: 2200px)").matches;

/**
 * To convert string to boolean
 * @param value
 */
export const parseBoolean = (value?: string) => (value ? value?.toLowerCase() === "true" : false);

/**
 * split without empty or undefined values
 * @param value
 */
export const flatSplit = (value?: string, target = "") => (value || "").split(target).filter(item => item) || [];

/**
 * Method use to pass for props not accept undefined
 */
export const doNothing = () => {};

export const getSystemZIndex = (key: string): number => {
    const zIndexValues = systemZIndex as any;
    return zIndexValues[key] || -1;
};

export const isNonEmptyString = (value: string | undefined) => value !== undefined && value.trim() !== "";

export const exportToCSV = async (fields: string[], data: (string | number | null)[][], filename?: string) => {
    const dataForUnparse = {
        fields,
        data,
    };

    const { unparse } = await import(/* webpackChunkName: "papaparse" */ "papaparse");
    const csv = unparse(dataForUnparse);
    const csvBlob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const fileName = `${filename || "csv_exported_file"}.csv`;
    const downloadLink = document.createElement("a");
    downloadLink.href = window.URL.createObjectURL(csvBlob);
    downloadLink.setAttribute("download", fileName);
    downloadLink.click();
};

const iOS = () => {
    return (
        ["iPad Simulator", "iPhone Simulator", "iPod Simulator", "iPad", "iPhone", "iPod"].includes(
            navigator.platform,
        ) ||
        // iPad on iOS 13 detection
        (navigator.userAgent.includes("Mac") && "ontouchend" in document)
    );
};

export const xOpenPrintDialog = () => {
    const firefoxPrintCallback = firefoxPrintFix();
    if (document.queryCommandSupported("print") && !iOS()) {
        document.execCommand("print", false);
    } else {
        window.print();
    }
    firefoxPrintCallback();
};

// trimming object key values that are strings
export function trimStringValuesInObject<T extends Record<string, any>>(modelObj: T): T {
    for (const key in modelObj) {
        // Check if the value is a string and trim it
        const value = modelObj[key];
        if (typeof value === "string") {
            modelObj[key] = value.trim();
        }
    }
    return modelObj;
}
